import ApiData from '@/api/ApiData'
import createXML from '@/services/data/KrpanoBuilder.js'
import Storage from '@/services/Storage'

export default {
  state: {
    isFetching: false,
    error: null,
    isTutorialVisible: false,
    introPopupShown: false,
    arModelIsFetching: false,
    popupIsFetching: false,
    popupError: null,
    virtualTourData: null,
    popupData: null,
    tour: '',
    isLogged: false,
    tourName: '',
    xmlString: '',
    orderedDesks: [],
    language: '',
    isMobileDevice: false,
    // Tour features that can be enabled or not!
    features: ['scene', 'lang', 'chat', 'wishlist', 'map', 'interactiveMap', 'authentication', 'analytics']
  },
  getters: {
    appIsFetching: state => state.isFetching,
    error: state => state.error,
    tour: state => state.tour,
    virtualTourData: state => state.virtualTourData,
    deskForMap: state =>
      state.virtualTourData && state.virtualTourData.contents
        ? state.virtualTourData.contents.filter(x => Boolean(x.deskAttributes))
        : [],
    isTutorialVisible: state => state.isTutorialVisible,
    popupData: state => state.popupData,
    popupError: state => state.popupError,
    popupIsFetching: state => state.popupIsFetching,
    arModelIsFetching: state => state.arModelIsFetching,
    tourName: state => state.virtualTourData.name,
    xmlString: state => state.xmlString,
    orderedDesks: state => state.orderedDesks,
    tourFeatures: state =>
      state.features.reduce(
        (res, x) => ({ ...res, [x]: (state.virtualTourData && state.virtualTourData.attributes[x]) || {} }),
        {}
      ),
    language: state => state.language,
    isMobileDevice: state => state.isMobileDevice,
    introPopupShown: state => state.introPopupShown
  },
  mutations: {
    IS_FETCHING(state, value = true) {
      state.isFetching = value
      state.error = null
    },
    MEDIA_HOTSPOTS_FETCHING(state, value = true) {
      state.isFetching = value
      state.error = null
    },
    POPUP_IS_FETCHING(state, value = true) {
      state.popupIsFetching = value
      state.popupError = null
    },
    AR_MODEL_IS_FETCHING(state, value = true) {
      state.arModelIsFetching = value
    },
    FETCH_VIRTUAL_TOUR_DATA_SUCCESS(state, payload) {
      state.virtualTourData = {
        ...payload.data,
        attributes: { ...payload.settings },
        assetsURL: `/contents/${process.env.VUE_APP_TOUR}/${payload.data.assetsURL}`
      }
      if (payload.data.slug) state.tour = payload.data.slug
      state.error = false
      state.isFetching = false
    },
    FETCH_MEDIA_HOTSPOTS_DATA_SUCCESS(state, payload) {
      state.virtualTourData.mediaHotspots = payload
    },
    FETCH_POPUP_DATA_SUCCESS(state, popupData) {
      state.popupError = null
      state.popupData = popupData
        ? {
            ...popupData,
            media: popupData.media ? popupData.media.map(x => `/contents/${process.env.VUE_APP_TOUR}/${x}`) : [],
            buttons: popupData.buttons && Array.isArray(popupData.buttons) ? popupData.buttons : []
          }
        : null
      state.popupIsFetching = false
    },
    FETCH_POPUP_DATA_ERROR(state, error) {
      state.popupError = error
      state.popupData = null
      state.popupIsFetching = false
    },
    SET_TOUR(state, tour) {
      state.tour = tour
    },
    SET_IS_TUTORIAL_VISIBLE(state, value) {
      state.isTutorialVisible = value
    },
    SET_ERROR(state, err) {
      state.error = err
      state.isFetching = false
    },
    KRPANO_XML_STRING_SUCCESS(state, xmlString) {
      state.xmlString = xmlString
    },
    SET_STARTING_PARAMS(state, params) {
      state.virtualTourData = {
        ...state.virtualTourData,
        attributes: {
          ...state.virtualTourData.attributes,
          scene: {
            ...state.virtualTourData.attributes.scene,
            startingHlookat: params.hlook || state.virtualTourData.attributes.scene.startingHlookat,
            startingViewPoint: params.vp || state.virtualTourData.attributes.scene.startingViewPoint
          }
        }
      }
    },
    SET_LANGUAGE(state, lang) {
      state.language = lang
    },
    SET_IS_MOBILE_DEVICE(state, value) {
      state.isMobileDevice = value
    },
    SET_INTRO_POPUP_SHOWN(state, value) {
      Storage.setItem('intro-popup-shown', true)
      state.introPopupShown = value
    },
    INIT_INTRO_POPUP_SHOWN(state) {
      if (Storage.getItem('intro-popup-shown')) state.introPopupShown = true
    }
  },
  actions: {
    APP_SET_PAGE_TITLE({ state }) {
      if (state.virtualTourData && state.virtualTourData.title) {
        document.title = `${state.virtualTourData.title} | ${document.title}`
      }
    },
    APP_IS_FETCHING({ commit }, value = true) {
      commit('IS_FETCHING', value)
    },
    APP_ERROR({ commit }, error) {
      commit('SET_ERROR', error)
    },
    SET_IS_TUTORIAL_VISIBLE({ commit }, value) {
      commit('SET_IS_TUTORIAL_VISIBLE', value)
    },
    SET_IS_POPUP_VISIBLE({ commit }, value) {
      commit('SET_IS_POPUP_VISIBLE', value)
    },
    FETCH_VIRTUAL_TOUR_DATA({ commit, state }, tour) {
      return new Promise(resolve => {
        if (state.virtualTourData) {
          resolve(true)
        } else {
          commit('IS_FETCHING')
          Promise.all([ApiData.getVirtualTourData(tour), ApiData.getVirtualTourSettings(tour)])
            .then(result => {
              commit('SET_TOUR', tour)
              commit('FETCH_VIRTUAL_TOUR_DATA_SUCCESS', { data: result[0], settings: result[1] })
              resolve(true)
            })
            .catch(error => {
              commit('SET_ERROR', error)
              resolve(false)
            })
        }
      })
    },
    SET_POPUP_IS_FETCHING({ commit }, value = true) {
      commit('POPUP_IS_FETCHING', value)
    },
    FETCH_POPUP_DATA({ commit, state }, popupId) {
      return new Promise(resolve => {
        const data = state.virtualTourData.contents.find(x => Array.isArray(x.tags) && x.tags.includes(popupId))
        const popupData = data ? { ...data, action: data.action || 'popup' } : null
        if (popupData && popupData.action === 'popup') {
          if (popupData.adapter) {
            commit('POPUP_IS_FETCHING', true)
            import(`@/adapters/${popupData.adapter}`)
              .then(adapter => {
                if (typeof adapter.fetchData === 'function') {
                  adapter
                    .fetchData(popupData)
                    .then(data => {
                      commit('FETCH_POPUP_DATA_SUCCESS', { ...data, currentTag: popupId })
                      commit('POPUP_IS_FETCHING', false)
                      resolve(data)
                    })
                    .catch(error => {
                      commit('POPUP_IS_FETCHING', false)
                      commit('SET_ERROR', error)
                      resolve(false)
                    })
                }
              })
              .catch(() => {
                commit('POPUP_IS_FETCHING', false)
                throw new Error(`Adapter not found: ${popupData.adapter}`)
              })
          } else {
            commit('FETCH_POPUP_DATA_SUCCESS', { ...popupData, currentTag: popupId })
            resolve(popupData)
          }
        }
      })
    },
    FETCH_MEDIA_HOTSPOTS_DATA({ commit, state }) {
      return new Promise(resolve => {
        const data = state.virtualTourData.mediaHotspots
        const mediaHotspotsAdapter = state.virtualTourData.mediaHotspotsAdapter

        if (data) {
          if (mediaHotspotsAdapter && mediaHotspotsAdapter.name && mediaHotspotsAdapter.dataUrl) {
            commit('MEDIA_HOTSPOTS_FETCHING', true)
            import(`@/adapters/${mediaHotspotsAdapter.name}`)
              .then(adapter => {
                if (typeof adapter.fetchMedia === 'function') {
                  adapter
                    .fetchMedia({ mediaHotspots: data, dataUrl: mediaHotspotsAdapter.dataUrl })
                    .then(data => {
                      commit('FETCH_MEDIA_HOTSPOTS_DATA_SUCCESS', data)
                      commit('MEDIA_HOTSPOTS_FETCHING', false)
                      resolve(data)
                    })
                    .catch(error => {
                      commit('SET_ERROR', error)
                      resolve(false)
                    })
                }
              })
              .catch(() => {
                commit('MEDIA_HOTSPOTS_FETCHING', false)
                throw new Error(`Adapter not found: ${mediaHotspotsAdapter.name}`)
              })
          } else {
            commit('FETCH_MEDIA_HOTSPOTS_DATA_SUCCESS', data)
            resolve(data)
          }
        }
      })
    },
    SET_POPUP_DATA({ commit }, popupData) {
      return new Promise(resolve => {
        commit('FETCH_POPUP_DATA_SUCCESS', popupData)
        resolve(true)
      })
    },
    KRPANO_XML_STRING({ commit, state }) {
      let xmlString = createXML(state.virtualTourData)
      commit('KRPANO_XML_STRING_SUCCESS', xmlString)
    },
    GET_AR_MODEL_LINK({ commit }, modelId) {
      commit('AR_MODEL_IS_FETCHING')
      return new Promise(resolve => {
        ApiData.getARModelLink(modelId)
          .then(result => {
            commit('AR_MODEL_IS_FETCHING', false)
            resolve(result)
          })
          .catch(err => {
            commit('AR_MODEL_IS_FETCHING', false)
            commit('FETCH_POPUP_DATA_ERROR', err.response.data)
            resolve(false)
          })
      })
    },
    SET_STARTING_PARAMS({ commit }, params) {
      commit('SET_STARTING_PARAMS', params)
    },
    SET_LANGUAGE({ commit }, lang) {
      commit('SET_LANGUAGE', lang)
    },
    SET_IS_MOBILE_DEVICE({ commit }, value) {
      commit('SET_IS_MOBILE_DEVICE', value)
    },
    SET_INTRO_POPUP_SHOWN({ commit }, value) {
      commit('SET_INTRO_POPUP_SHOWN', value)
    },
    INIT_INTRO_POPUP_SHOWN({ commit }) {
      commit('INIT_INTRO_POPUP_SHOWN')
    }
  }
}
